import {
  // 旧格式 icon-* 映射保留的图标
  Mail,
  Calendar,
  CalendarClock,
  CalendarDays,
  Star,
  CheckSquare,
  Map,
  Building2,
  Target,
  BookOpen,
  Shield,
  Crown,
  FileText,
  Settings,
  User,
  LogOut,
  Tag,
  Tags,
  ShoppingBag,
  // Nav Menu UI handoff §07 图标对照表（lucide）
  House,
  Headphones,
  ClipboardList,
  Sparkle,
  Wallet,
  Package,
  Settings2,
  Server,
  Inbox,
  LayoutDashboard,
  MessageSquare,
  CalendarCheck,
  LifeBuoy,
  Users,
  Phone,
  PhoneCall,
  ListChecks,
  Building,
  Key,
  ClipboardCheck,
  Repeat,
  Briefcase,
  Archive,
  Layers,
  Barcode,
  Folder,
  ArrowLeftRight,
  ShoppingCart,
  Book,
  Copy,
  GitBranch,
  Table,
  Wrench,
  Activity,
  Zap,
  Bell,
  SquareCheckBig,
} from "lucide-react"

import { icons, type LucideIcon } from "lucide-react"

const iconMap: Record<string, LucideIcon> = {
  // 分组图标（旧格式兼容）
  'icon-customer-service': Headphones,
  'icon-operation': ClipboardList,
  'icon-guest-experience': Sparkle,
  'icon-system': Settings2,
  'icon-logout': LogOut,

  // 菜单图标（旧格式兼容）
  'icon-dashboard': LayoutDashboard,
  'icon-mail-open': Mail,
  'icon-calendar': Calendar,
  'icon-star': Star,
  'icon-user': User,
  'icon-task': CheckSquare,
  'icon-map': Map,
  'icon-home': Building2,
  'icon-message': Target,
  'icon-book': BookOpen,
  'icon-team': Shield,
  'icon-crown': Crown,
  'icon-bank': Building2,
  'icon-eye': FileText,
  'icon-setting': Settings,
  'icon-tag': Tag,
  'icon-tags': Tags,
  'icon-schedule': CalendarClock,
  'icon-shopping-bag': ShoppingBag,

  // lucide 改过名的旧名：`icons` 表只收新名，按旧名查会回落 FileText。后端菜单种子仍在用旧名。
  // CheckSquare → SquareCheckBig（lucide-react 具名导出 CheckSquare 就是它的别名）—— Assessments 菜单。
  'CheckSquare': SquareCheckBig,
  'check-square': SquareCheckBig,
}

// 路由级图标映射 —— Nav Menu UI handoff §07 图标对照表的落地。
// 后端 icon 字段不够细分/格式不一时，按 routePath 给每个菜单项精确的 lucide 图标。
const routeIconOverrides: Record<string, LucideIcon> = {
  // My Workspace
  '/my-tasks': Inbox,
  // Customer Service
  '/dashboard': LayoutDashboard,
  '/inbox': MessageSquare,
  '/reservations': CalendarCheck,
  '/reviews': Star,
  '/support-cases': LifeBuoy,
  '/guests': Users,
  '/intents': Target,
  '/intake-requests': Target,
  '/call-logs': Phone,
  '/call-routing': PhoneCall,
  '/phone-numbers': PhoneCall,
  // Operation Center
  '/tasks': FileText,
  '/task-calendar': Calendar,
  '/task-triage': ListChecks,
  '/listings': Building,
  '/smart-locks': Key,
  '/material-requests': Package,
  '/assessments': SquareCheckBig,
  // Guest Experience
  '/guest-guide': BookOpen,
  '/upsell-requests': ShoppingBag,
  '/concierge-requests': Bell,
  // Accounting & HR
  '/staff-attendance': ClipboardCheck,
  '/shifts': CalendarDays,
  '/shift-requests': Repeat,
  '/attendance-requests': CalendarCheck,
  '/owners': Briefcase,
  // Inventory & Procurement
  '/asset-mgr/inventory': Archive,
  '/asset-mgr/assets': Layers,
  '/asset-mgr/products': Barcode,
  '/asset-mgr/categories': Folder,
  '/asset-mgr/transactions': ArrowLeftRight,
  '/procurement': ShoppingCart,
  // Account Settings
  '/roles-permissions': Shield,
  '/users': User,
  '/catalogs': Book,
  '/task-templates': Copy,
  '/workflows': GitBranch,
  '/system/tags': Tag,
  '/views': Table,
  '/settings': Settings,
  // Platform
  '/tenants': Building2,
  '/jobs-config': Wrench,
  '/system/activity-event-types': Activity,
  '/ops/eventbus': Zap,
}

// 分组图标 —— handoff §07：按分组名映射（My Workspace / Platform 为提议图标，库里缺）
const groupIconMap: Record<string, LucideIcon> = {
  'my workspace': House,
  'customer service': Headphones,
  'operation center': ClipboardList,
  'guest experience': Sparkle,
  'accounting & hr': Wallet,
  'inventory & procurement': Package,
  'account settings': Settings2,
  'platform': Server,
}

/** kebab-case lucide 名（如 "layout-dashboard"）→ PascalCase 组件 key */
const kebabToPascal = (name: string): string =>
  name.split('-').map((s) => s.charAt(0).toUpperCase() + s.slice(1)).join('')

/**
 * 获取菜单项图标组件
 * @param iconName 后端返回的图标字符串：旧格式 "icon-dashboard" 或 lucide 名（kebab/Pascal）
 * @param routePath 菜单路径，用于路径级别的图标映射（优先级最高）
 */
export const getIconComponent = (iconName: string | null | undefined, routePath?: string | null): LucideIcon => {
  if (routePath && routeIconOverrides[routePath]) return routeIconOverrides[routePath]
  if (!iconName) return FileText
  // 优先查旧格式映射表（兼容），再查 lucide 组件名（Pascal 或 kebab）
  if (iconMap[iconName]) return iconMap[iconName]
  const lucideIcon = icons[iconName as keyof typeof icons]
    ?? icons[kebabToPascal(iconName) as keyof typeof icons]
  if (lucideIcon) return lucideIcon as LucideIcon
  return FileText
}

/**
 * 获取分组图标组件（侧栏分组标题行 / 收起态 rail 用）
 * @param label 分组名（菜单目录 name）
 * @param iconName 后端目录的 icon 字段（有合法值时优先）
 */
export const getGroupIconComponent = (label: string, iconName?: string | null): LucideIcon => {
  if (iconName) {
    if (iconMap[iconName]) return iconMap[iconName]
    const lucideIcon = icons[iconName as keyof typeof icons]
      ?? icons[kebabToPascal(iconName) as keyof typeof icons]
    if (lucideIcon) return lucideIcon as LucideIcon
  }
  const byLabel = groupIconMap[label.trim().toLowerCase()]
  if (byLabel) return byLabel
  return Folder
}
